/**
 * Secret Retriever Configuration (Frontend Wrapper)
 * 
 * In the Frontend React App, secrets are pre-fetched by the 
 * build script (scripts/fetch-secrets.js) and injected into 
 * process.env at build time with the REACT_APP_ prefix.
 * 
 * This file provides the same API as the backend for seamless usage.
 */

let initialized = false;

// We do not need a true async init in the frontend since
// the secrets are already embedded during build, but we keep 
// the signature similar. It is synchronous here.
function initSecrets() {
  if (initialized) return;
  initialized = true;
  console.info("[SecretRetriever] Secrets initialized from build environment.");
}

/**
 * Fetch one or multiple secrets
 * @param {string|string[]} names 
 * @returns {any} secret value or object of values
 */
function fetchSecret(names) {
  if (!initialized) {
    console.warn("[SecretRetriever] secrets not initialized. Call initSecrets() first.");
    // Auto-initialize
    initSecrets();
  }

  if (!names) {
    console.error("[SecretRetriever] Secret names must be provided");
    throw new Error("Secret names must be provided");
  }

  // Webpack statically replaces process.env, so we cannot use dynamic keys like process.env[`REACT_APP_${name}`].
  // We must map them statically.
  const envMap = {
    "FIREBASE_API_KEY": process.env.REACT_APP_PUBLIC_FIREBASE_API_KEY || process.env.REACT_APP_FIREBASE_API_KEY,
    "FIREBASE_AUTH_DOMAIN": process.env.REACT_APP_PUBLIC_FIREBASE_AUTH_DOMAIN || process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    "FIREBASE_PROJECT_ID": process.env.REACT_APP_PUBLIC_FIREBASE_PROJECT_ID || process.env.REACT_APP_FIREBASE_PROJECT_ID,
    "FIREBASE_STORAGE_BUCKET": process.env.REACT_APP_PUBLIC_FIREBASE_STORAGE_BUCKET || process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    "FIREBASE_MESSAGING_SENDER_ID": process.env.REACT_APP_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    "FIREBASE_APP_ID": process.env.REACT_APP_PUBLIC_FIREBASE_APP_ID || process.env.REACT_APP_FIREBASE_APP_ID,
    "FIREBASE_MEASUREMENT_ID": process.env.REACT_APP_PUBLIC_FIREBASE_MEASUREMENT_ID || process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    "PUBLIC_BASE_PATH": process.env.REACT_APP_PUBLIC_BASE_PATH,
  };

  const getSecretValue = (name) => {
    const val = envMap[name] || null;
    return val;
  };

  if (Array.isArray(names)) {
    return names.reduce((acc, name) => {
      acc[name] = getSecretValue(name);
      if (acc[name] === null) {
        console.warn(`[SecretRetriever] Secret '${name}' not found in front-end env.`);
      }
      return acc;
    }, {});
  }

  // Single string lookup
  if (typeof names === "string") {
    const val = getSecretValue(names);
    if (!val) {
      console.warn(`[SecretRetriever] Secret '${names}' not found in front-end env.`);
    }
    return val;
  }

  throw new Error("Invalid secret name(s)");
}

export { initSecrets, fetchSecret };
